(function () {
  if (window.ebSearchScriptLoaded) return;
  window.ebSearchScriptLoaded = true;

  class EbSearchOverlay extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.eb-search-overlay__panel');
      this.input = this.querySelector('[data-eb-search-input]');
      this.results = this.querySelector('[data-eb-search-results]');
      this.form = this.querySelector('[data-eb-search-form]');
      this.recentKey = 'eb:recent-searches';
      this.requestId = 0;
      this.lastTrigger = null;
      this.isDesignMode = Boolean(window.Shopify?.designMode);
      this.bind();
      this.renderRecent();
    }

    bind() {
      document.addEventListener('eb:search:open', (event) => {
        this.lastTrigger = event.target instanceof HTMLElement ? event.target : document.activeElement;
        this.open();
      });

      document.addEventListener('keydown', (event) => {
        if (this.isTypingTarget(event.target)) return;
        if (event.key === '/' || ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k')) {
          event.preventDefault();
          this.lastTrigger = document.activeElement;
          this.open();
        }
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-eb-search-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.target.closest('[data-eb-search-clear]')) {
          event.preventDefault();
          this.clearInput();
          return;
        }

        const queryButton = event.target.closest('[data-eb-search-query]');
        if (queryButton) {
          this.input.value = queryButton.dataset.ebSearchQuery;
          this.submit();
        }

        const recentRemove = event.target.closest('[data-eb-recent-remove]');
        if (recentRemove) {
          this.removeRecent(recentRemove.dataset.query);
          this.renderRecent();
        }

        if (event.target.closest('[data-eb-recent-clear]')) {
          this.clearRecent();
          this.renderRecent();
        }
      });

      this.input?.addEventListener('input', this.debounce(() => this.updatePredictive(), 200));
      this.input?.addEventListener('keydown', (event) => this.handleInputKeydown(event));
      this.form?.addEventListener('submit', (event) => {
        if (this.input?.value.trim()) {
          event.preventDefault();
          this.submit();
        }
      });
      this.results?.addEventListener('scroll', () => this.input?.blur(), { passive: true });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (!this.isDesignMode) {
        window.ebScrollLock?.lock();
        this.setPageInert(true);
      }
      window.setTimeout(() => this.input?.focus({ preventScroll: true }), 30);
    }

    close() {
      const wasOpen = this.classList.contains('is-open');
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (wasOpen && !this.isDesignMode) window.ebScrollLock?.unlock();
      if (!this.isDesignMode) this.setPageInert(false);
      if (wasOpen) this.dispatchEvent(new CustomEvent('eb:search:close', { bubbles: true }));
      this.lastTrigger?.focus?.({ preventScroll: true });
    }

    clearInput() {
      if (!this.input?.value) {
        this.close();
        return;
      }
      this.input.value = '';
      this.updatePredictive();
      this.input.focus();
    }

    handleInputKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.clearInput();
        return;
      }

      if (event.key === 'Enter') {
        if (document.activeElement === this.input) {
          event.preventDefault();
          this.submit();
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const options = this.options();
      if (!options.length) return;
      const next = event.key === 'ArrowDown' ? 0 : options.length - 1;
      options[next].focus();
    }

    options() {
      return [...this.querySelectorAll('[data-eb-search-option]')].filter((node) => node.offsetParent !== null);
    }

    async updatePredictive() {
      const query = this.input?.value.trim() || '';
      if (query.length < Number(this.dataset.minChars || 2)) {
        await this.fetchPredictive('');
        return;
      }
      await this.fetchPredictive(query);
    }

    async fetchPredictive(query) {
      const id = ++this.requestId;
      const base = query ? this.dataset.predictiveUrl : window.location.pathname;
      const url = new URL(base || '/search/suggest', window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (query) {
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', this.dataset.predictiveTypes || 'product');
        url.searchParams.set('resources[limit]', this.dataset.productsLimit || '4');
      }
      this.classList.toggle('is-loading', Boolean(query));
      const html = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((response) => response.text()).catch(() => '');
      if (id !== this.requestId || !html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-eb-predictive-content]');
      const current = this.querySelector('[data-eb-predictive-content]');
      if (fresh && current) {
        current.replaceWith(fresh);
        this.dispatchAnalytics(query, Number(fresh.dataset.resultCount || 0));
      }
      this.classList.remove('is-loading');
      this.renderRecent();
    }

    submit() {
      const query = this.input?.value.trim();
      if (!query) return;
      this.storeRecent(query);
      this.dispatchAnalytics(query, null, 'submit');
      const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      url.searchParams.set('q', query);
      window.location.href = url.toString();
    }

    renderRecent() {
      if (this.dataset.enableRecent !== 'true') return;
      const root = this.querySelector('[data-eb-recent-searches]');
      const list = this.querySelector('[data-eb-recent-list]');
      if (!root || !list) return;
      const items = this.getRecent();
      root.hidden = !items.length;
      list.innerHTML = items.map((query) => `
        <span class="eb-search-recent">
          <button type="button" data-eb-search-query="${this.escape(query)}">${this.escape(query)}</button>
          <button class="eb-search-recent__remove" type="button" data-eb-recent-remove data-query="${this.escape(query)}" aria-label="${this.escape(this.dataset.recentRemoveLabel)} ${this.escape(query)}">x</button>
        </span>
      `).join('');
    }

    getRecent() {
      try {
        return JSON.parse(localStorage.getItem(this.recentKey) || '[]');
      } catch (error) {
        return [];
      }
    }

    storeRecent(query) {
      if (this.dataset.enableRecent !== 'true') return;
      try {
        const next = [query, ...this.getRecent().filter((item) => item.toLowerCase() !== query.toLowerCase())].slice(0, 4);
        localStorage.setItem(this.recentKey, JSON.stringify(next));
      } catch (error) {}
    }

    removeRecent(query) {
      try {
        localStorage.setItem(this.recentKey, JSON.stringify(this.getRecent().filter((item) => item !== query)));
      } catch (error) {}
    }

    clearRecent() {
      try {
        localStorage.removeItem(this.recentKey);
      } catch (error) {}
    }

    dispatchAnalytics(query, count, action = 'predictive') {
      if (!query) return;
      const detail = { source: this.dataset.analyticsSource, action, query };
      if (Number.isFinite(count)) detail.results_count = count;
      document.dispatchEvent(new CustomEvent('eb:analytics', { detail }));
      if (count === 0) document.dispatchEvent(new CustomEvent('eb:analytics', { detail: { source: this.dataset.analyticsSource, action: 'zero_results', query, results_count: 0 } }));
    }

    setPageInert(state) {
      [...document.body.children].forEach((child) => {
        if (child === this || child.contains(this) || child.tagName === 'SCRIPT') return;
        if (state) child.setAttribute('inert', '');
        else child.removeAttribute('inert');
      });
    }

    trapFocus(event) {
      const focusable = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    isTypingTarget(target) {
      return target?.matches?.('input, textarea, select, [contenteditable="true"]');
    }

    debounce(callback, wait) {
      let timer;
      return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => callback(...args), wait);
      };
    }

    escape(value) {
      return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char]);
    }
  }

  class EbMainSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-eb-search-page-form]');
      this.input = this.querySelector('[data-eb-search-page-input]');
      this.form?.addEventListener('submit', (event) => {
        const query = this.input?.value.trim();
        if (!query) return;
        document.dispatchEvent(new CustomEvent('eb:analytics', { detail: { source: 'search_page', action: 'submit', query } }));
      });
      if (this.dataset.resultsCount === '0' && this.dataset.query) {
        document.dispatchEvent(new CustomEvent('eb:analytics', { detail: { source: 'search_page', action: 'zero_results', query: this.dataset.query, results_count: 0 } }));
      }
    }
  }

  if (!customElements.get('eb-search-overlay')) customElements.define('eb-search-overlay', EbSearchOverlay);
  if (!customElements.get('eb-main-search')) customElements.define('eb-main-search', EbMainSearch);
})();
